import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import IconAddCircle from "@/assets/icons/addCircle.svg?react";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import DisplayGroupItem from "@/components/admin/displayGroup/DisplayGroupItem";
import PageHeader from "@/components/admin/header/PageHeader";
import { Input } from "@/components/atomic/Input";
import LoadingSpinner from "@/components/atomic/Loading";
import Button from "@/components/ui/Button";
import { routes } from "@/configs/router.config/routes";
import { useDisplayGroups } from "@/hooks/useDisplayGroups";
import { useLandingPaths } from "@/hooks/useLandingPaths";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { DisplayGroup } from "@/models/displayGroup";
import { DisplayGroupService } from "@/services/displayGroup/displayGroup.service";
import { showError, showSuccess } from "@/utils/notification";

const DisplayGroupPage: React.FC = () => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const [opened, setOpened] = useState(false);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [isDeletingDisplayGroup, setIsDeletingDisplayGroup] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const navigate = useNavigate();
  const { displayGroups, isLoading, mutate: mutateDisplayGroups } = useDisplayGroups();
  const { landingPaths, mutate: mutateLandingPaths } = useLandingPaths();

  const handleOpenDeleteDialog = (id: string) => {
    setTargetId(id);
    setOpened(true);
  };

  const handleDelete = async (id: string) => {
    try {
      setIsDeletingDisplayGroup(true);
      await DisplayGroupService.delete(id);
      await Promise.all([mutateDisplayGroups(), mutateLandingPaths()]);
      showSuccess("表示グループを削除しました。");
    } catch (error) {
      console.error(error);
      showError("表示グループの削除に失敗しました。");
    } finally {
      setIsDeletingDisplayGroup(false);
      setOpened(false);
    }
  };

  // AccordionTableで使用するデータを構築
  const buildAccordionData = (displayGroup: DisplayGroup) => {
    const data: Record<string, { groups: string[]; versions: string[]; }> = {};

    // 現在の表示グループに関連するカテゴリパスを処理
    displayGroup.categoryPaths.forEach(path => {
      const landingPath = landingPaths?.find(lp => lp.path === path);
      if (landingPath) {
        // 他の表示グループに割り当てられているかチェック
        const otherGroups = displayGroups?.filter(dg =>
          dg.id !== displayGroup.id && dg.categoryPaths.includes(path)
        ).map(dg => dg.name) ?? [];

        data[path] = {
          groups: otherGroups,
          versions: landingPath.containedVersions,
        };
      }
    });

    return data;
  };

  // アコーディオン表示用のアイテムを作成
  const accordionItems = displayGroups?.map((dg: DisplayGroup) => {
    const accordionData = buildAccordionData(dg);

    return (
      <DisplayGroupItem
        key={dg.id}
        displayGroup={dg}
        accordionData={accordionData}
        currentWorkspaceId={sessionCurrentWorkspaceId}
        onDelete={handleOpenDeleteDialog}
      />
    );
  });

  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="flex items-center justify-between w-full">
        <PageHeader title="表示グループ管理" />
        <Button
          disabled={isLoading}
          onClick={() => setIsCreateModalOpen(true)}
          className={cn(
            "w-50 h-10 px-4 py-2 gap-1.5 !text-sm text-white !font-bold",
            "bg-[#293d72] border-[#293d72] hover:bg-[#293d72]/90 shadow-[0px_2px_#293d72]",
          )}
        >
          <IconAddCircle className="scale-75" />
          表示グループの追加
        </Button>
      </div>

      {/* アコーディオン形式の表示 */}
      {!isLoading && (
        <div className="flex flex-col gap-6 w-full items-start">
          {accordionItems}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center items-center h-[100px]">
          <LoadingSpinner data-testid="loader" />
        </div>
      )}

      <ConfirmDialog
        opened={opened}
        title="表示グループの削除"
        message={`「${displayGroups?.find((dg: DisplayGroup) => dg.id === targetId)?.name ?? ""}」を削除しますか？`}
        confirmColor="red"
        confirmText="削除する"
        onConfirm={() => targetId && handleDelete(targetId)}
        onClose={() => setOpened(false)}
        loading={isDeletingDisplayGroup}
      />

      <ConfirmDialog
        opened={isCreateModalOpen}
        title="表示グループを追加"
        message={
          <div className="flex flex-col gap-4">
            <p className="text-[14px] text-[#333333]">
              表示グループ名を入力してください。
            </p>
            <Input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className={cn(
                "w-full h-[60px] px-6 py-4 border border-[#6888e1] rounded-[5px] !text-[18px] text-[#5F6368]",
                "outline-none focus:shadow-[0px_0px_5px_0px_#6888e1]",
              )}
              placeholder=""
              autoFocus
            />
          </div>
        }
        disabled={!groupName.trim()}
        confirmText="続けてカテゴリを選択"
        confirmColor="blue"
        onClose={() => {
          setIsCreateModalOpen(false);
          setGroupName("");
        }}
        onConfirm={() => {
          navigate(`${routes.displayGroupCreate(sessionCurrentWorkspaceId)}?name=${encodeURIComponent(groupName)}`);
          setIsCreateModalOpen(false);
          setGroupName("");
        }}
      />
    </div>
  );
};

export default DisplayGroupPage;
